// Conta e perfil. Em produção, o Supabase com Google (a conta anônima de antes só guarda a conta, pela vinculação
// manual). Em desenvolvimento, a mesa local.
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { GRITOS, type Grito } from '@truco6/mesa/protocolo';
import { ehPlano, type Plano } from '@truco6/planos';
import { ErroDaApi, api, lerEu } from './api.ts';
import { pedirCelularNaVolta } from './celular.ts';
import type { Origem } from './chegada.ts';
import { config } from './config.ts';
import { ErroDaConta, PADRAO_GOOGLE, codigoDoErro, mensagemDoErro } from './erros-conta.ts';

export interface Sessao {
  id: string;
  anonimo: boolean; // conta anônima de antes do fim do convidado: só vê a tela de guardar a conta
  nome: string | null; // nome da conta Google, para sugerir o apelido
  email?: string | null; // e-mail da conta Google, só para a própria pessoa (Configurações)
  token(): Promise<string>;
}

export interface PerfilConta {
  apelido: string;
  avatar: string | null; // 'av-bi' (avatar do elenco) ou 'foto-<versão>' (foto enviada)
  cidadeIbge: number | null; // código IBGE de 7 dígitos (packages/localidades)
  rankingRegionalEm: string | null; // "Aparecer no ranking da cidade": ligada desde (nasce ligada com a cidade)
  rankingRegionalDesligadoEm: string | null; // quem desligou fica desligado, mesmo trocando de cidade
  rankingRegionalAviso: boolean; // ligada pela migração: o Início avisa uma vez
  cidadeRevisar: boolean; // a cidade antiga, em texto, não bateu com a lista: o perfil pede de novo
  plano: Plano; // plano vigente: faturas pagas e concessões (plano_de no banco)
  gritos: Partial<Record<Grito, string>>; // versão de cada grito gravado
  vozConsentidaEm: string | null; // consentimento para a voz tocar na mesa (LGPD)
  zoeira: boolean; // "Zoeira na mesa" (Configurações): desligada, a pessoa não vê nem manda zoeira
}

// Convite para uma sala (com o código da mesa) ou indicação do jogo (indique e ganhe).
export interface Convite {
  canal: 'whatsapp' | 'link';
  tipo: 'sala' | 'indicacao';
  mesa?: string;
}

export const PERFIL_VAZIO: Pick<PerfilConta, 'plano' | 'gritos' | 'vozConsentidaEm' | 'cidadeIbge' | 'rankingRegionalEm' | 'rankingRegionalDesligadoEm'
  | 'rankingRegionalAviso' | 'cidadeRevisar' | 'zoeira'> = {
  plano: 'gratis', gritos: {}, vozConsentidaEm: null, cidadeIbge: null, rankingRegionalEm: null, rankingRegionalDesligadoEm: null,
  rankingRegionalAviso: false, cidadeRevisar: false, zoeira: true,
};

type OpcaoDaCidade = Pick<PerfilConta, 'cidadeIbge' | 'rankingRegionalEm' | 'rankingRegionalDesligadoEm' | 'rankingRegionalAviso'>;

// A cidade muda, como o banco faz: a primeira cidade liga a opção (se a pessoa nunca desligou), trocar de cidade
// mantém a opção como estava e, sem cidade, a opção fica vazia.
export function comCidade<T extends OpcaoDaCidade>(p: T, cidadeIbge: number | null): T {
  if (!cidadeIbge) return { ...p, cidadeIbge: null, rankingRegionalEm: null, rankingRegionalAviso: false };
  if (!p.cidadeIbge && !p.rankingRegionalEm && !p.rankingRegionalDesligadoEm) return { ...p, cidadeIbge, rankingRegionalEm: new Date().toISOString() };
  return { ...p, cidadeIbge };
}

// Ligar ou desligar "Aparecer no ranking da cidade". Desligar guarda a data; qualquer toque tira o aviso do Início.
export function comRanking<T extends OpcaoDaCidade>(p: T, ligado: boolean): T {
  const agora = new Date().toISOString();
  return ligado && p.cidadeIbge
    ? { ...p, rankingRegionalEm: p.rankingRegionalEm ?? agora, rankingRegionalDesligadoEm: null, rankingRegionalAviso: false }
    : { ...p, rankingRegionalEm: null, rankingRegionalDesligadoEm: p.rankingRegionalDesligadoEm ?? agora, rankingRegionalAviso: false };
}

// Tipo do arquivo sem os parâmetros ('audio/webm;codecs=opus' vira 'audio/webm'), como o bucket espera.
const tipoBase = (b: Blob) => (b.type || 'audio/webm').split(';')[0]!.trim();
const novaVersao = () => Date.now().toString(36);

export interface Conta {
  atual(): Promise<Sessao | null>;
  ouvir(fn: (s: Sessao | null) => void): () => void;
  entrarComGoogle(volta: string): Promise<void>;
  guardarComGoogle(volta: string): Promise<void>;
  sair(): Promise<void>;
  excluirConta(): Promise<void>;
  lerPerfil(s: Sessao): Promise<PerfilConta | null>;
  salvarPerfil(s: Sessao, p: PerfilConta): Promise<void>;
  // Configurações e o aviso do Início: true liga, false desliga, null só tira o aviso.
  rankingRegional(s: Sessao, ligado: boolean | null): Promise<void>;
  // "Zoeira na mesa" em Configurações: vale na mesa a partir da próxima mão.
  zoeira(s: Sessao, ligada: boolean): Promise<void>;
  enviarFoto(s: Sessao, foto: Blob): Promise<string>;
  urlFoto(usuario: string, avatar: string | null): string | null;
  registrarConvite(s: Sessao, convite: Convite): Promise<void>;
  registrarOrigem(s: Sessao, o: Origem): Promise<void>; // cadastro_origem, uma vez por conta
  consentirVoz(s: Sessao, sim: boolean): Promise<string | null>; // sem consentimento, os gritos são apagados
  enviarGrito(s: Sessao, grito: Grito, audio: Blob, atuais: PerfilConta['gritos']): Promise<PerfilConta['gritos']>;
  apagarGrito(s: Sessao, grito: Grito, atuais: PerfilConta['gritos']): Promise<PerfilConta['gritos']>;
  urlGrito(usuario: string, grito: Grito, versao: string): string | null;
}

// Criar e editar o perfil. Sem cidade, sem ranking regional; com cidade, a chave como está na tela.
async function salvarNaApi(s: Sessao, p: PerfilConta): Promise<void> {
  await api(s, 'POST', '/perfil', {
    apelido: p.apelido, avatar: p.avatar, cidadeIbge: p.cidadeIbge, ...(p.cidadeIbge ? { rankingRegional: !!p.rankingRegionalEm } : {}),
  });
}
const rankingNaApi = (s: Sessao, ligado: boolean | null) => api(s, 'POST', '/perfil/ranking-regional', ligado === null ? {} : { ligado });
const zoeiraNaApi = (s: Sessao, ligada: boolean) => api(s, 'POST', '/perfil/zoeira', { ligada });

// ---------- Supabase ----------
function contaSupabase(url: string, anon: string): Conta {
  const sb: SupabaseClient = createClient(url, anon, { auth: { persistSession: true, detectSessionInUrl: true, flowType: 'pkce' } });
  const token = async () => {
    const { data } = await sb.auth.getSession();
    if (!data.session) throw new Error('sem sessão');
    return data.session.access_token;
  };
  const sessao = (u: { id: string; is_anonymous?: boolean; email?: string; user_metadata?: Record<string, unknown> } | null | undefined): Sessao | null =>
    u ? { id: u.id, anonimo: !!u.is_anonymous, nome: (u.user_metadata?.full_name as string) || (u.user_metadata?.name as string) || null, email: u.email || null, token } : null;
  // Nenhum texto do Supabase chega à tela: o erro vira frase em português (conhecido) ou a frase padrão da ação.
  const falha = (e: { message: string; code?: string } | null, padrao: string) => {
    if (e) throw new ErroDaConta(mensagemDoErro(e.code, e.message, padrao), codigoDoErro(e.code, e.message));
  };
  const PERFIL = 'Não deu para salvar o perfil. Tente de novo.', GRITO = 'Não deu para salvar o grito. Tente de novo.';
  return {
    async atual() { const { data } = await sb.auth.getSession(); return sessao(data.session?.user); },
    ouvir(fn) {
      const { data } = sb.auth.onAuthStateChange((_ev, s) => fn(sessao(s?.user)));
      return () => data.subscription.unsubscribe();
    },
    async entrarComGoogle(volta) { falha((await sb.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: volta } })).error, PADRAO_GOOGLE); },
    // A conta anônima de antes guardando a conta: vincula o Google à mesma conta (precisa da vinculação manual ligada no
    // Supabase). Só a tela de guardar a conta chama.
    async guardarComGoogle(volta) {
      pedirCelularNaVolta();
      falha((await sb.auth.linkIdentity({ provider: 'google', options: { redirectTo: volta } })).error, PADRAO_GOOGLE);
    },
    async sair() { await sb.auth.signOut(); },
    async excluirConta() {
      const { data } = await sb.auth.getSession();
      if (data.session) {
        const id = data.session.user.id;
        await sb.storage.from('rostos').remove([id + '.webp']);
        await sb.storage.from('gritos').remove(GRITOS.map((g) => id + '/' + g));
      }
      // A API cancela a assinatura na Stripe antes de excluir. Se ela estiver fora, o banco exclui quem não assina.
      const s = data.session && sessao(data.session.user);
      try {
        if (!s) throw new ErroDaApi(401, 'nao_autenticado', 'Entre na sua conta.');
        await api(s, 'POST', '/conta/excluir');
      } catch (e) {
        if (!(e instanceof ErroDaApi) || (e.codigo !== 'fora_do_ar' && e.codigo !== 'sem_conexao')) throw e;
        falha((await sb.rpc('excluir_conta')).error, 'Não deu para excluir a conta. Tente de novo.');
      }
      await sb.auth.signOut();
    },
    async lerPerfil(s) {
      type Linha = { apelido: string; avatar: string | null; gritos: unknown; voz_consentida_em: string | null; plano_vigente: unknown;
        cidade_ibge?: number | null; ranking_regional_em?: string | null; cidade_revisar?: boolean;
        ranking_regional_desligado_em?: string | null; ranking_regional_aviso?: boolean; zoeira?: boolean };
      const CIDADE = 'cidade_ibge, ranking_regional_em, cidade_revisar, ';
      const OPCAO = 'ranking_regional_desligado_em, ranking_regional_aviso, ';
      const ler = (cidade: string) => sb.from('perfis').select(`apelido, avatar, ${cidade}gritos, voz_consentida_em, plano_vigente`)
        .eq('id', s.id).maybeSingle<Linha>();
      let { data, error } = await ler(CIDADE + OPCAO + 'zoeira, ');
      // Banco ainda sem a chave da zoeira: lê sem ela (fica ligada).
      if (error && /zoeira/.test(error.message)) ({ data, error } = await ler(CIDADE + OPCAO));
      // Banco ainda sem as migrações da cidade (deploy do jogo antes do banco): lê sem a opção nova e, se faltar, sem a cidade.
      if (error && /ranking_regional_desligado_em|ranking_regional_aviso/.test(error.message)) ({ data, error } = await ler(CIDADE));
      if (error && /cidade_ibge|ranking_regional_em|cidade_revisar/.test(error.message)) ({ data, error } = await ler(''));
      falha(error, 'Não deu para carregar seu perfil. Tente de novo.');
      if (!data) return null;
      return {
        apelido: data.apelido, avatar: data.avatar, plano: ehPlano(data.plano_vigente) ? data.plano_vigente : 'gratis',
        gritos: (data.gritos ?? {}) as PerfilConta['gritos'], vozConsentidaEm: data.voz_consentida_em,
        cidadeIbge: data.cidade_ibge ?? null, rankingRegionalEm: data.ranking_regional_em ?? null, cidadeRevisar: !!data.cidade_revisar,
        rankingRegionalDesligadoEm: data.ranking_regional_desligado_em ?? null, rankingRegionalAviso: !!data.ranking_regional_aviso,
        zoeira: data.zoeira !== false,
      };
    },
    // O apelido só entra no banco pela API, que confere o filtro de palavras. Salvar resolve o aviso da cidade antiga.
    async salvarPerfil(s, p) { await salvarNaApi(s, p); },
    async rankingRegional(s, ligado) { await rankingNaApi(s, ligado); },
    async zoeira(s, ligada) { await zoeiraNaApi(s, ligada); },
    async enviarFoto(s, foto) {
      falha((await sb.storage.from('rostos').upload(s.id + '.webp', foto, { upsert: true, contentType: foto.type })).error, 'Não deu para enviar a foto. Tente de novo.');
      return 'foto-' + Date.now().toString(36);
    },
    urlFoto(usuario, avatar) {
      return avatar?.startsWith('foto-') ? `${url}/storage/v1/object/public/rostos/${usuario}.webp?v=${avatar.slice(5)}` : null;
    },
    async registrarConvite(s, { canal, tipo, mesa }) {
      // Evento para o painel. Se falhar, o convite segue: não vale travar a mesa por isso.
      await sb.from('eventos').insert({ tipo: 'convite_enviado', usuario: s.id, mesa: mesa ?? null, dados: { canal, tipo } });
    },
    async registrarOrigem(_s, o) { falha((await sb.rpc('registrar_cadastro_origem', { p_origem: o.origem, p_cta: o.cta })).error, ''); },
    async consentirVoz(s, sim) {
      if (!sim) await sb.storage.from('gritos').remove(GRITOS.map((g) => s.id + '/' + g));
      const { data, error } = await sb.from('perfis').update({ voz_consentida_em: sim ? new Date().toISOString() : null })
        .eq('id', s.id).select('voz_consentida_em').single();
      falha(error, PERFIL);
      return (data as { voz_consentida_em: string | null }).voz_consentida_em;
    },
    async enviarGrito(s, grito, audio, atuais) {
      falha((await sb.storage.from('gritos').upload(s.id + '/' + grito, audio, { upsert: true, contentType: tipoBase(audio) })).error, GRITO);
      const gritos = { ...atuais, [grito]: novaVersao() };
      falha((await sb.from('perfis').update({ gritos }).eq('id', s.id)).error, GRITO);
      return gritos;
    },
    async apagarGrito(s, grito, atuais) {
      falha((await sb.storage.from('gritos').remove([s.id + '/' + grito])).error, GRITO);
      const gritos = { ...atuais };
      delete gritos[grito];
      falha((await sb.from('perfis').update({ gritos }).eq('id', s.id)).error, GRITO);
      return gritos;
    },
    urlGrito(usuario, grito, versao) {
      return `${url}/storage/v1/object/public/gritos/${usuario}/${grito}?v=${versao}`;
    },
  };
}

// ---------- local (desenvolvimento e testes de ponta a ponta) ----------
const gravacoesLocais = new Map<string, string>();

function contaLocal(): Conta {
  const CHAVE = 'truco6:conta-local', PERFIL = 'truco6:perfil-local';
  const ouvintes = new Set<(s: Sessao | null) => void>();
  const ler = (): { id: string; anonimo: boolean } | null => {
    try { return JSON.parse(localStorage.getItem(CHAVE) ?? 'null'); } catch { return null; }
  };
  // A conta local é sempre Google. Só os testes gravam uma conta com anonimo, para a tela de guardar a conta.
  const sessao = (c: { id: string; anonimo: boolean } | null): Sessao | null => c ? {
    ...c, anonimo: !!c.anonimo, nome: null, email: c.anonimo ? null : `${c.id}@local.trucode6.com.br`,
    async token() {
      const r = await fetch(`/mesa/dev/token?id=${c.id}&anonimo=${c.anonimo ? 1 : 0}`);
      return ((await r.json()) as { token: string }).token;
    },
  } : null;
  const gravar = (c: { id: string; anonimo: boolean } | null) => {
    if (c) localStorage.setItem(CHAVE, JSON.stringify(c)); else localStorage.removeItem(CHAVE);
    for (const f of ouvintes) f(sessao(c));
  };
  const novoId = () => 'dev-' + Math.random().toString(36).slice(2, 10);
  // 'truco6:erro-google-local' simula a resposta do Supabase: o erro na hora (vinculação desligada) ou na volta do
  // Google (identity_already_exists volta na URL, como em produção).
  const erroSimulado = () => {
    const codigo = localStorage.getItem('truco6:erro-google-local');
    if (!codigo) return;
    if (codigo === 'identity_already_exists') {
      location.assign('/?error=server_error&error_code=identity_already_exists&error_description=Identity+is+already+linked+to+another+user');
      return new Promise<never>(() => {});
    }
    throw new ErroDaConta(mensagemDoErro(codigo, 'Manual linking is disabled'), codigo);
  };
  return {
    async atual() { return sessao(ler()); },
    ouvir(fn) { ouvintes.add(fn); return () => ouvintes.delete(fn); },
    // Como o Supabase, volta para o endereço pedido (o link da sala, com ?vaga=link) quando ele é outro.
    async entrarComGoogle(volta) {
      gravar({ id: ler()?.id ?? novoId(), anonimo: false });
      const u = new URL(volta, location.href);
      if (u.pathname + u.search !== location.pathname + location.search) location.assign(u.pathname + u.search);
    },
    async guardarComGoogle() { await erroSimulado(); pedirCelularNaVolta(); const c = ler(); if (c) gravar({ ...c, anonimo: false }); },
    async sair() { gravar(null); localStorage.removeItem(PERFIL); },
    async excluirConta() {
      const s = sessao(ler());
      if (s) await api(s, 'POST', '/conta/excluir').catch(() => {});
      gravar(null); localStorage.removeItem(PERFIL);
    },
    async lerPerfil(s) {
      let p: PerfilConta | null = null;
      try { p = JSON.parse(localStorage.getItem(PERFIL) ?? 'null'); } catch { p = null; }
      if (!p) return null;
      // Plano: 'truco6:plano-local' no navegador manda; senão, a API local (Stripe simulada); senão, VITE_PLANO_LOCAL.
      const escolhido = localStorage.getItem('truco6:plano-local');
      const plano = escolhido ?? (await lerEu(s).then((e) => e.plano, () => null)) ?? config.planoLocal;
      return { ...PERFIL_VAZIO, ...p, plano: ehPlano(plano) ? plano : 'gratis' };
    },
    // A API local confere o apelido como em produção; sem ela no ar, o perfil fica só no aparelho.
    async salvarPerfil(s, p) {
      await salvarNaApi(s, p).catch((e) => {
        if (!(e instanceof ErroDaApi) || (e.codigo !== 'fora_do_ar' && e.codigo !== 'sem_conexao')) throw e;
      });
      localStorage.setItem(PERFIL, JSON.stringify({ ...p, rankingRegionalEm: p.cidadeIbge ? p.rankingRegionalEm : null, cidadeRevisar: false }));
    },
    async rankingRegional(s, ligado) {
      await rankingNaApi(s, ligado).catch((e) => {
        if (!(e instanceof ErroDaApi) || (e.codigo !== 'fora_do_ar' && e.codigo !== 'sem_conexao' && e.codigo !== 'sem_perfil')) throw e;
      });
      const p = { ...PERFIL_VAZIO, ...JSON.parse(localStorage.getItem(PERFIL) ?? '{}') } as PerfilConta;
      localStorage.setItem(PERFIL, JSON.stringify(ligado === null ? { ...p, rankingRegionalAviso: false } : comRanking(p, ligado)));
    },
    // A API local guarda para a mesa local; sem ela no ar, a chave fica só no aparelho.
    async zoeira(s, ligada) {
      await zoeiraNaApi(s, ligada).catch((e) => {
        if (!(e instanceof ErroDaApi) || (e.codigo !== 'fora_do_ar' && e.codigo !== 'sem_conexao' && e.codigo !== 'sem_perfil')) throw e;
      });
      const p = JSON.parse(localStorage.getItem(PERFIL) ?? '{}') as Partial<PerfilConta>;
      localStorage.setItem(PERFIL, JSON.stringify({ ...p, zoeira: ligada }));
    },
    async enviarFoto() { throw new Error('Foto só funciona com o Supabase.'); },
    urlFoto() { return null; },
    async registrarConvite() {},
    async registrarOrigem() {},
    // Gritos ficam só nesta aba (endereço de blob): servem para testar a gravação sem Supabase.
    async consentirVoz(_s, sim) {
      const p = JSON.parse(localStorage.getItem(PERFIL) ?? '{}') as Partial<PerfilConta>;
      const quando = sim ? new Date().toISOString() : null;
      localStorage.setItem(PERFIL, JSON.stringify({ ...p, vozConsentidaEm: quando, gritos: sim ? p.gritos ?? {} : {} }));
      if (!sim) gravacoesLocais.clear();
      return quando;
    },
    async enviarGrito(_s, grito, audio, atuais) {
      const versao = novaVersao();
      gravacoesLocais.set(grito + ':' + versao, URL.createObjectURL(audio));
      const gritos = { ...atuais, [grito]: versao };
      const p = JSON.parse(localStorage.getItem(PERFIL) ?? '{}') as Partial<PerfilConta>;
      localStorage.setItem(PERFIL, JSON.stringify({ ...p, gritos }));
      return gritos;
    },
    async apagarGrito(_s, grito, atuais) {
      const gritos = { ...atuais };
      delete gritos[grito];
      const p = JSON.parse(localStorage.getItem(PERFIL) ?? '{}') as Partial<PerfilConta>;
      localStorage.setItem(PERFIL, JSON.stringify({ ...p, gritos }));
      return gritos;
    },
    urlGrito(_usuario, grito, versao) { return gravacoesLocais.get(grito + ':' + versao) ?? null; },
  };
}

export const conta: Conta = config.authLocal || !config.supabaseUrl
  ? contaLocal()
  : contaSupabase(config.supabaseUrl, config.supabaseAnon);
